import { useEffect } from 'react'
import type { CSSProperties, JSX } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  ArrowLeftIcon,
  ChartColumnIcon,
  Button,
  EmptyState,
  Flex,
  Link,
  LoaderCircleIcon,
  PlusIcon,
} from '@buffer-mono/popcorn'

import { useChannels } from '~publish/components/MainSidebar/components/ChannelsSection/useChannels'
import { getChannelName } from '~publish/helpers/channels'
import {
  buildAnalyzeChannelUrl,
  buildAnalyzeHomeUrl,
} from '~publish/helpers/externalUrls'
import { Service } from '~publish/legacy/constants/services/ServiceDefinitions'

import styles from './AnalyticsPage.module.css'

const hasService = (state: unknown): state is { service: string } => {
  return (
    typeof state === 'object' &&
    state !== null &&
    'service' in state &&
    typeof state.service === 'string'
  )
}

const CONNECT_CHANNEL_CTA = 'publish-analyticsComingSoon-connectChannel-1'

/**
 * Analytics Page
 *
 * This page handles analytics routing for a channel:
 * - If the channel has analytics access, it redirects to the external Analyze app
 * - If the channel doesn't have analytics, it shows the "coming soon" UI
 */
const EMPTY_STATE_STYLE: CSSProperties & Record<`--${string}`, string> = {
  '--empty-state-icon-container-size': '60px',
  '--empty-state-icon-size': '24px',
}

export function AnalyticsPage(): JSX.Element {
  const { id: channelId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Get channels with products data to check analytics availability
  const { channels, loading: channelsLoading } = useChannels()

  // Find the current channel and check if it has analytics
  const channelFromList = channels.find((c) => c.id === channelId)
  const hasAnalytics = channelFromList?.products?.includes('analyze') ?? false

  // Handle redirect to external Analyze app if channel has analytics
  useEffect(() => {
    if (!channelId || !channelFromList || channelsLoading) return

    if (hasAnalytics) {
      // Try to get service from location state first

      const service = hasService(location.state)
        ? location.state.service
        : channelFromList.service

      if (service) {
        window.location.href = buildAnalyzeChannelUrl(channelId, service)
      } else {
        // Fallback to analyze home if channel service can't be determined
        window.location.href = buildAnalyzeHomeUrl()
      }
    }
  }, [
    channelId,
    channelFromList,
    hasAnalytics,
    channelsLoading,
    location.state,
  ])

  // Navigate back to the root of the application
  const handleBackToPublish = (): void => {
    navigate('..')
  }

  // Open the channel storefront dialog to connect a new channel
  const handleConnectChannel = (): void => {
    const { actions } = window?.appshell || {}
    actions?.openChannelStorefront({
      cta: CONNECT_CHANNEL_CTA,
    })
  }

  // Show loading state while checking analytics availability or redirecting
  if (channelsLoading || !channelFromList || hasAnalytics) {
    return (
      <Flex align="center" justify="center" fullHeight fullWidth>
        <LoaderCircleIcon size="large" />
      </Flex>
    )
  }

  // At this point, channelFromList is guaranteed to exist and not have analytics
  const channel = channelFromList
  const channelName = getChannelName(channel.service)

  // Check if this is a reminder-only channel (Instagram Personal or Facebook Group)
  const isInstagramPersonal =
    channel.service === Service.Instagram && channel.type === 'profile'
  const isFacebookGroup =
    channel.service === Service.Facebook && channel.type === 'group'
  const isReminderOnlyChannel = isInstagramPersonal || isFacebookGroup

  // Different messaging for reminder-only channels
  if (isReminderOnlyChannel) {
    return (
      <Flex align="center" justify="center" fullHeight fullWidth>
        <EmptyState size="xlarge" variant="warning" style={EMPTY_STATE_STYLE}>
          <EmptyState.Icon className={styles.squareIcon}>
            <ChartColumnIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            Analytics aren&apos;t available for {channelName}
          </EmptyState.Heading>
          <EmptyState.Description>
            {isInstagramPersonal ? (
              <>
                Instagram Personal Profiles have limited API access and only
                support scheduling posts. To unlock analytics and more features,
                consider{' '}
                <Link
                  href="https://help.buffer.com/article/290-connect-instagram-profiles-and-business-pages-to-buffer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  switching to a Creator or Business account
                </Link>
                .
              </>
            ) : (
              <>
                Facebook Groups have limited API access and only support
                scheduling posts. Analytics are not available for this channel
                type.
              </>
            )}
          </EmptyState.Description>
          <EmptyState.Actions>
            <Button variant="secondary" onClick={handleBackToPublish}>
              <ArrowLeftIcon /> Back to Publish
            </Button>
            <Button variant="primary" onClick={handleConnectChannel}>
              <PlusIcon />
              Connect Channel
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      </Flex>
    )
  }

  return (
    <Flex align="center" justify="center" fullHeight fullWidth>
      <EmptyState size="xlarge" variant="warning" style={EMPTY_STATE_STYLE}>
        <EmptyState.Icon className={styles.squareIcon}>
          <ChartColumnIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>
          Analytics support coming soon for {channelName}
        </EmptyState.Heading>
        <EmptyState.Actions>
          <Button variant="secondary" onClick={handleBackToPublish}>
            <ArrowLeftIcon /> Back to Publish
          </Button>
          <Button variant="primary" onClick={handleConnectChannel}>
            <PlusIcon />
            Connect Channel
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    </Flex>
  )
}
